import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi, errorMessage } from "../../lib/api";

const when = (s) => (s ? new Date(s).toLocaleString("en-IN") : "—");
const TAG = { approved: ["Exited", "bg-emerald-600"], rejected: ["Rejected", "bg-red-600"] };

export const ExitsDialog = ({ open, onClose, onChange }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { onChange?.(); return adminApi.get("/admin/exits").then((r) => setItems(r.data)).catch(() => {}); }, [onChange]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);
  const decide = (x, action) => {
    if (action === "reject" && !window.confirm("Reject this exit? The placement stays active and commission continues.")) return;
    adminApi.post(`/admin/exits/${x.id}`, { action })
      .then(() => { toast.success(action === "approve" ? "Approved. Exit OTP sent to the employer." : "Exit request rejected"); load(); })
      .catch((e) => toast.error(errorMessage(e)));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="exits-dialog" className="max-h-[90vh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] p-4 text-slate-100 sm:p-6">
        <DialogHeader><DialogTitle className="font-heading text-xl text-white">Exit requests</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Approve sends the exit OTP to the employer (portal + WhatsApp). Reject keeps the placement active. Until the employer enters the OTP, monthly commission continues.</p>
        {items.length === 0 && <p className="text-slate-500">No exit requests yet.</p>}
        <div className="space-y-2">
          {items.map((x) => (
            <div key={x.id} data-testid={`exit-row-${x.id}`} className="border border-white/10 px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span><b className="text-white">{x.worker_name}</b> ({x.worker_reference}) · {x.job_role} · {x.company_name}</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase text-white ${(TAG[x.request_status] || ["", x.admin_approved ? "bg-blue-600" : "bg-amber-600"])[1]}`}>
                  {(TAG[x.request_status] || [x.admin_approved ? "Approved · OTP sent" : "Waiting"])[0]}</span>
              </div>
              <p className="text-slate-300">Reason: {x.reason_label}{x.note ? ` · ${x.note}` : ""}</p>
              <p className="text-xs text-slate-500">Requested {when(x.requested_at)}{x.sent_via ? ` · alert via ${x.sent_via === "none" ? "— (Telegram & WhatsApp not set)" : x.sent_via}` : ""}{x.decided_at ? ` · decided ${when(x.decided_at)} by ${x.decided_by}` : ""}{x.approved_at ? ` · exited ${when(x.approved_at)}` : ""} · joined {when(x.started_at)}</p>
              {x.request_status === "pending" && !x.admin_approved && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => decide(x, "approve")} data-testid={`exit-approve-${x.id}`} className="bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Approve (send OTP)</button>
                  <button onClick={() => decide(x, "reject")} data-testid={`exit-reject-${x.id}`} className="bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700">Reject</button>
                </div>
              )}
              {x.request_status === "pending" && x.admin_approved && x.otp && <p className="mt-1 font-mono text-sm text-blue-400" data-testid={`exit-otp-${x.id}`}>OTP sent to employer: {x.otp}</p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
